import { useDraggable } from '@dnd-kit/core';
import * as React from 'react';

import { Box } from '../../components/ui/box';
import { cn } from '../../lib/utils';

import { GRID_TILE_ITEM_KEY, GridDndScopeContext } from './grid-dnd-scope';
import { GRID_LAYER } from './grid-layers-z';
import type { ResizeDirection } from './grid-gesture';
import { SortableGridContext } from './sortable-grid';
import type { Omittable } from "../../lib/props";
import { sortableStyle } from "../../lib/dnd-attrs";

// A child tile: reads its placement from grid context by id, exposes drag-handle
// props, and renders edge resize handles when the grid is manageable. The
// dragged tile follows the pointer via a translate transform while siblings
// spring-reflow to the live preview; on drop the grid commits the new placement.

/** The enclosing `SortableGridItem`'s id, provided around its rendered
 *  children. Stack zones inside a tile read it to classify "my own container
 *  is the thing being dragged" (self-drop exclusion) without any consumer
 *  plumbing. Null outside a grid item. */
export const GridItemIdContext = React.createContext<string | null>(null);

export type SortableGridDragHandleProps = Omittable<{
   
  onPointerDown?: (event: any) => void;
   
  onKeyDown?: (event: any) => void;
  /** The activator node — what the keyboard sensor anchors its coordinate
   *  steps and live-region announcements to. A callback rather than
   *  `Ref<HTMLElement>`: the handle can be any element, and only the callback
   *  form is assignable to the `Ref<HTMLDivElement>` a `Box` asks for. */
  ref?: (node: HTMLElement | null) => void;
  /** Present only while the tile is arrangeable. A drag handle a keyboard
   *  cannot reach has no keyboard drag, however well `onKeyDown` behaves. */
  tabIndex?: number;
  'aria-roledescription'?: string;
  'aria-describedby'?: string;
}>;

export interface SortableGridItemRenderArgs {
  dragHandleProps: SortableGridDragHandleProps;
  isDragging: boolean;
  isResizing: boolean;
  /** Live (preview-aware) span. Tracks the previewed cell WHILE resizing — pass
   *  it to the body's tier resolution so content re-tiers in real time, not only
   *  after the gesture commits. Unchanged during a move (only x/y move). */
  w: number;
  h: number;
}

type SortableGridItemProps = Omittable<{
  /** Stable id — must match an entry in the parent SortableGrid's `layout`. */
  id: string;
  /** Disable drag/resize for this specific item. */
  disabled?: boolean;
  /** Consumer payload this tile's drag carries (`gridTileItem`), letting it
   *  ride OTHER grids' external phantom path — page tiles dropping into
   *  containers, nested children dragging out to the page. The tile's drag
   *  stays internal to its OWN grid (id ownership wins in routing). */
  externalItem?: unknown;
  /** Either a static node or a render prop that receives drag handle props. */
  children:
    | React.ReactNode
    | ((args: SortableGridItemRenderArgs) => React.ReactNode);
}>;

const SortableGridItem = React.memo(function SortableGridItem({
  id,
  disabled,
  externalItem,
  children,
}: SortableGridItemProps) {
  const ctx = React.useContext(SortableGridContext);
  if (!ctx) {
    throw new Error('SortableGridItem must be used within a SortableGrid');
  }
  const scope = React.useContext(GridDndScopeContext);

  const manageable = ctx.canManage && !disabled && !ctx.singleColumn;

  const dragData = React.useMemo(
    () =>
      externalItem !== undefined ? { [GRID_TILE_ITEM_KEY]: externalItem } : {},
    [externalItem],
  );
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform } =
    useDraggable({
      id,
      data: dragData,
      disabled: !manageable,
    });

  // Merge the dnd-kit ref with the grid's node registry (FLIP measurement +
  // fit-to-content).
  const { registerNode } = ctx;
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      registerNode(id, node);
    },
    [setNodeRef, registerNode, id],
  );

  const isDragging = ctx.activeMode === 'drag' && ctx.activeId === id;
  const isResizing = ctx.activeMode === 'resize' && ctx.activeId === id;
  const overlayDrag = ctx.dragPresentation === 'overlay';

  const dragHandleProps = React.useMemo<SortableGridDragHandleProps>(
    () => ({
      ...(listeners as unknown as SortableGridDragHandleProps),
      // A non-arrangeable tile gets neither: a focus stop on a header that
      // cannot be picked up is a dead tab stop, and there is no activator to
      // register when there is no drag.
      ref: manageable ? setActivatorNodeRef : undefined,
      tabIndex: manageable ? attributes.tabIndex : undefined,
      'aria-roledescription': attributes['aria-roledescription'] ?? undefined,
      'aria-describedby': attributes['aria-describedby'] ?? undefined,
    }),
    [listeners, attributes, manageable, setActivatorNodeRef],
  );

  // Live (preview-aware) span — drives consumer tier resolution so a widget body
  // re-tiers in real time while being resized. Tracks the previewed w/h during a
  // resize; unchanged during a move (which only shifts x/y).
  const livePlacement = ctx.placement(id);
  const liveW = livePlacement?.w ?? 2;
  const liveH = livePlacement?.h ?? 2;

  const renderedChildren = (
    <GridItemIdContext.Provider value={id}>
      {typeof children === 'function'
        ? children({ dragHandleProps, isDragging, isResizing, w: liveW, h: liveH })
        : children}
    </GridItemIdContext.Provider>
  );

  // Overlay presentation: while dragging, the clone rides the scope's single
  // portaled DragOverlay (the GridPaletteItem pattern) — container cards are
  // `overflow: hidden`, so a translate-following nested tile would clip at
  // the card edge. Held in a ref so a re-render mid-drag never tears the
  // overlay down.
  const overlayContentRef = React.useRef<React.ReactNode>(null);
  overlayContentRef.current = renderedChildren;
  const publishOverlay = overlayDrag && isDragging && scope != null;
  React.useLayoutEffect(() => {
    if (!publishOverlay || !scope) return;
    scope.setOverlay(overlayContentRef.current);
    return () => scope.setOverlay(null);
  }, [publishOverlay, scope]);

  // Single-column (mobile): full-width stacked rows, height derived from the
  // row span. DOM order = visual order — the consumer mounts its items
  // pre-sorted by (y, x), so no CSS `order` is involved and the accessibility
  // tree walks the visual sequence.
  if (ctx.singleColumn) {
    const cell = ctx.placement(id);
    const h = cell?.h ?? 2;
    // Content-derived items (`autoH`) skip the fixed height and simply size to
    // their content in the page flow; everything else derives height from the
    // row span so the stack keeps the grid's vertical rhythm.
    const autoH = ctx.constraintsFor(id).autoH === true;
    const height = autoH ? undefined : h * ctx.rowHeight + (h - 1) * ctx.gapPx;
    return (
      <Box position="relative" ref={setRefs} style={{ height }}>
        {renderedChildren}
      </Box>
    );
  }

  // The dragged tile anchors to its original cell and follows the pointer via a
  // translate transform; everything else (including a resizing tile) renders at
  // its live placement. Anchoring to the origin keeps the translate 1:1 —
  // reading the previewed target here would double-count the delta and the tile
  // would race away at twice pointer speed.
  const cell = (isDragging ? ctx.activeOrigin : null) ?? ctx.placement(id);
  const x = cell?.x ?? 0;
  const y = cell?.y ?? 0;
  const w = cell?.w ?? 2;
  const h = cell?.h ?? 2;

  // Under overlay presentation the tile stays anchored (the DragOverlay clone
  // follows the pointer) and dims like a lifted palette card; translating it
  // too would move the ghost at double speed.
  const transformStr =
    isDragging && !overlayDrag
      ? sortableStyle(transform, { translateOnly: true }).transform
      : undefined;

  // Resize affordances honor the span constraints: height-locked tiles
  // (derived-height containers) expose east/west only; width-locked tiles
  // (nested column children) expose the south edge only.
  const constraints = ctx.constraintsFor(id);
  const widthLocked =
    constraints.minW != null && constraints.minW === constraints.maxW;
  // Content-derived items (`autoH`) own their rows, so they keep exactly what a
  // height-locked tile keeps: the two side edges. A card whose height follows
  // its content is still a card someone wants wider, and a side edge that does
  // nothing reads as a card that cannot be resized at all — a host width menu
  // is a second door, not the only one. A host that snaps widths (half/full)
  // snaps the commit; the drag itself still tracks the pointer.
  const resizeDirections: ResizeDirection[] =
    constraints.lockH || constraints.autoH
      ? widthLocked
        ? []
        : ['e', 'w']
      : widthLocked
        ? ['s']
        : RESIZE_HANDLE_ALL;

  return (
    <Box position="relative"
      ref={setRefs}
      style={{
        gridColumnStart: x + 1,
        gridColumnEnd: `span ${w}`,
        gridRowStart: y + 1,
        gridRowEnd: `span ${h}`,
        transform: transformStr || undefined,
        opacity: overlayDrag && isDragging ? 0.4 : undefined,
        // The active tile rides above its siblings; dimming is left to the
        // consumer via the `isDragging` render arg.
        zIndex: isDragging
          ? GRID_LAYER.draggingTile
          : isResizing
            ? GRID_LAYER.resizingTile
            : undefined,
      }}
    >
      {renderedChildren}
      {manageable
        ? resizeDirections.map((dir) => (
            <ResizeHandle
              key={dir}
              direction={dir}
              onPointerDown={(e) => ctx.beginResize(id, dir, e)}
            />
          ))
        : null}
    </Box>
  );
});

SortableGridItem.displayName = 'SortableGridItem';

// Thin invisible hit strip with a visible cue on hover. Lives on the grid item
// itself so resizing works regardless of body content.

const EDGE_BASE = {
  position: 'absolute',
  touchAction: 'none',
  zIndex: GRID_LAYER.edgeHandle,
} as const;
const CORNER_BASE = {
  position: 'absolute',
  width: 14,
  height: 14,
  touchAction: 'none',
  zIndex: GRID_LAYER.cornerHandle,
} as const;

const RESIZE_HANDLE_STYLES: Record<ResizeDirection, React.CSSProperties> = {
  n: { ...EDGE_BASE, left: 6, right: 6, top: -3, height: 8, cursor: 'ns-resize' },
  s: { ...EDGE_BASE, left: 6, right: 6, bottom: -3, height: 8, cursor: 'ns-resize' },
  e: { ...EDGE_BASE, top: 6, bottom: 6, right: -3, width: 8, cursor: 'ew-resize' },
  w: { ...EDGE_BASE, top: 6, bottom: 6, left: -3, width: 8, cursor: 'ew-resize' },
  ne: { ...CORNER_BASE, right: -4, top: -4, cursor: 'nesw-resize' },
  nw: { ...CORNER_BASE, left: -4, top: -4, cursor: 'nwse-resize' },
  se: { ...CORNER_BASE, right: -4, bottom: -4, cursor: 'nwse-resize' },
  sw: { ...CORNER_BASE, left: -4, bottom: -4, cursor: 'nesw-resize' },
};

const RESIZE_HANDLE_ALL: ResizeDirection[] = [
  'n',
  's',
  'e',
  'w',
  'ne',
  'nw',
  'se',
  'sw',
];

// The faint hover cue inside each handle: a thin centered line for edges, a
// small square tucked into the corner for corners.
const RESIZE_CUE_CLASSES: Record<ResizeDirection, string> = {
  n: 'left-1/2 -translate-x-1/2 top-1 h-0.5 w-6',
  s: 'left-1/2 -translate-x-1/2 bottom-1 h-0.5 w-6',
  e: 'top-1/2 -translate-y-1/2 right-1 w-0.5 h-6',
  w: 'top-1/2 -translate-y-1/2 left-1 w-0.5 h-6',
  ne: 'right-1 top-1 w-2 h-2',
  nw: 'left-1 top-1 w-2 h-2',
  se: 'right-1 bottom-1 w-2 h-2',
  sw: 'left-1 bottom-1 w-2 h-2',
};

function ResizeHandle({
  direction,
  onPointerDown,
}: {
  direction: ResizeDirection;
  onPointerDown: (event: React.PointerEvent) => void;
}) {
  return (
    <Box group="resize"
      onPointerDown={onPointerDown}
      style={RESIZE_HANDLE_STYLES[direction]}
    >
      <Box hoverReveal="resize" position="absolute" duration={150} rounded="full"
        className={cn(
          'bg-foreground/40',
          RESIZE_CUE_CLASSES[direction],
        )}
      />
    </Box>
  );
}

export { SortableGridItem };
export type { SortableGridItemProps };
